// Curvas de crecimiento del PDF firmado: 056A/056B (Adolescente: Peso/Edad, Talla/Edad, IMC/Edad y Velocidad de crecimiento), Fenton (016B/016C/028A-1) y OMS (028B/028C).
// El cliente arma los puntos (referencia OMS/NCHS/Orbegozo + mediciones del paciente); aquí solo se dibujan, con `pdf-lib`, en el mismo
// estilo vectorial que `graficoConstantesUci.ts` y `partograma.ts`. Puro: sin I/O. Equivalencia funcional, no facsímil de la lámina.
import { rgb, type PDFFont, type PDFPage } from 'pdf-lib';
import { BORDE_SUTIL, GRIS_OSCURO, INK, MUTED } from './layout';

/** `referencia` = línea fina continua; `referenciaDiscontinua` = línea fina discontinua (p. ej. lo leído de la curva); `paciente` = línea gruesa con puntos. */
export type EstiloSerieCurvaPdf = 'referencia' | 'referenciaDiscontinua' | 'paciente';

export interface PuntoCurvaPdf {
  readonly x: number;
  readonly y: number;
}

/** Colores fijos para distinguir series en una misma gráfica (p. ej. pulso en rojo y temperatura en azul en la curva térmica del 020). */
export type ColorSerieCurvaPdf = 'rojo' | 'azul';

export interface SerieCurvaPdf {
  readonly etiqueta: string;
  readonly estilo: EstiloSerieCurvaPdf;
  /** Sin color: `paciente` va en verde y las referencias en gris. */
  readonly color?: ColorSerieCurvaPdf;
  /** Los puntos están en las unidades del `ejeYSecundario` de la gráfica (se dibujan sobre la misma cuadrícula). */
  readonly ejeSecundario?: boolean;
  readonly puntos: readonly PuntoCurvaPdf[];
}

/** Segunda escala vertical sobre la misma cuadrícula (el papel del 020 imprime la del pulso y la de la temperatura una junto a la otra). */
export interface EjeYSecundarioPdf {
  readonly etiqueta: string;
  readonly yMin: number;
  readonly yMax: number;
  /** Paso de las marcas (p. ej. 1 °C); sin él se elige uno "redondo". */
  readonly paso?: number;
  readonly color?: ColorSerieCurvaPdf;
}

export interface GraficaCurvaPdf {
  readonly titulo: string;
  readonly nota?: string;
  readonly ejeXEtiqueta: string;
  readonly ejeYEtiqueta: string;
  readonly xMin: number;
  readonly xMax: number;
  readonly yMin: number;
  readonly yMax: number;
  /** Paso de las marcas del eje X (p. ej. 2 semanas, 6 meses, 1 año). Sin él se elige uno "redondo" (1, 2, 5, 10 por la potencia de diez que corresponda). */
  readonly pasoX?: number;
  /** Escala adicional a la izquierda de la principal; las series con `ejeSecundario` se leen en ella. */
  readonly ejeYSecundario?: EjeYSecundarioPdf;
  readonly series: readonly SerieCurvaPdf[];
}

/** Wire format serializable de `EspecificacionPdf` (modos 'msp' y 'pares', campo `curvasCrecimiento`) — 056A/056B, Fenton y OMS. */
export interface DatosCurvasCrecimientoPdf {
  readonly graficas: readonly GraficaCurvaPdf[];
}

export const ESTILOS_CURVA_VALIDOS: ReadonlySet<string> = new Set<EstiloSerieCurvaPdf>(['referencia', 'referenciaDiscontinua', 'paciente']);

const ALTO_TITULO = 12;
const ALTO_NOTA = 9;
const ALTO_PLOT = 120;
const ALTO_EJE_X = 22;
const MARGEN_IZQ = 26;
const MARGEN_DER = 26;
const SEPARACION = 8;

const PINE = rgb(0x1f / 255, 0x4d / 255, 0x47 / 255);
const ROJO = rgb(0.78, 0.1, 0.1);
const AZUL = rgb(0.1, 0.25, 0.75);
const COLORES_SERIE = { rojo: ROJO, azul: AZUL } as const;
export const COLORES_SERIE_VALIDOS: ReadonlySet<string> = new Set<ColorSerieCurvaPdf>(['rojo', 'azul']);
const ANCHO_EJE_SECUNDARIO = 22;

/** Espacio vertical de una gráfica: reservarlo con `cursor.dibujo(altoDibujoCurva(g), ...)`. */
export function altoDibujoCurva(g: GraficaCurvaPdf): number {
  return ALTO_TITULO + (g.nota ? ALTO_NOTA : 0) + ALTO_PLOT + ALTO_EJE_X + SEPARACION;
}

/** Marcas "redondas" del eje entre `min` y `max` (unas 5-6): el paso es 1, 2, 5 o 10 (por la potencia de diez que corresponda). */
export function marcasEje(min: number, max: number, pasoFijo?: number): number[] {
  const rango = max - min;
  if (!(rango > 0)) return [min];
  const bruto = rango / 5;
  const potencia = 10 ** Math.floor(Math.log10(bruto));
  const paso = pasoFijo !== undefined && pasoFijo > 0 ? pasoFijo : ([1, 2, 5, 10].map((m) => m * potencia).find((p) => p >= bruto) ?? 10 * potencia);
  const marcas: number[] = [];
  for (let v = Math.ceil(min / paso - 1e-9) * paso; v <= max + 1e-9 && marcas.length < 50; v += paso) marcas.push(Math.round(v * 1000) / 1000 + 0); // + 0: evita -0
  return marcas;
}

const formato = (v: number): string => (Number.isInteger(v) ? String(v) : String(Math.round(v * 10) / 10));

/** Dibuja una gráfica de curvas (referencia + paciente) sobre una `PDFPage` cruda, entregada por `CursorPdf.dibujo()`. */
export function dibujarCurva(pagina: PDFPage, origen: { x: number; yTop: number }, anchoDisponible: number, fontRegular: PDFFont, g: GraficaCurvaPdf): void {
  let y = origen.yTop - 9;
  pagina.drawText(g.titulo, { x: origen.x, y, size: 9, font: fontRegular, color: GRIS_OSCURO });
  y -= ALTO_TITULO - 9 + 2;
  if (g.nota) {
    pagina.drawText(g.nota, { x: origen.x, y: y - 6, size: 6.5, font: fontRegular, color: MUTED });
    y -= ALTO_NOTA;
  }

  const margenIzq = MARGEN_IZQ + (g.ejeYSecundario ? ANCHO_EJE_SECUNDARIO : 0);
  const plotX = origen.x + margenIzq;
  const plotW = anchoDisponible - margenIzq - MARGEN_DER;
  const plotTop = y - 2;
  const plotBottom = plotTop - ALTO_PLOT;
  const xDe = (v: number): number => plotX + ((Math.min(Math.max(v, g.xMin), g.xMax) - g.xMin) / (g.xMax - g.xMin)) * plotW;
  const yDe = (v: number): number => plotBottom + ((Math.min(Math.max(v, g.yMin), g.yMax) - g.yMin) / (g.yMax - g.yMin)) * ALTO_PLOT;

  pagina.drawRectangle({ x: plotX, y: plotBottom, width: plotW, height: ALTO_PLOT, borderColor: BORDE_SUTIL, borderWidth: 0.5 });
  const eje2 = g.ejeYSecundario;
  const aY2 = (v: number): number => {
    if (!eje2) return v;
    return g.yMin + ((Math.min(Math.max(v, eje2.yMin), eje2.yMax) - eje2.yMin) / (eje2.yMax - eje2.yMin)) * (g.yMax - g.yMin);
  };
  for (const v of marcasEje(g.yMin, g.yMax)) {
    const yy = yDe(v);
    pagina.drawLine({ start: { x: plotX, y: yy }, end: { x: plotX + plotW, y: yy }, thickness: 0.25, color: BORDE_SUTIL });
    pagina.drawText(formato(v), { x: origen.x + MARGEN_IZQ - 4 - formato(v).length * 3.2, y: yy - 2, size: 6, font: fontRegular, color: eje2 ? ROJO : MUTED });
  }
  if (eje2) {
    const colorEje = eje2.color ? COLORES_SERIE[eje2.color] : MUTED;
    for (const v of marcasEje(eje2.yMin, eje2.yMax, eje2.paso)) {
      const yy = yDe(aY2(v));
      pagina.drawLine({ start: { x: plotX - 3, y: yy }, end: { x: plotX, y: yy }, thickness: 0.5, color: colorEje });
      pagina.drawText(formato(v), { x: plotX - 5 - formato(v).length * 3.2, y: yy - 2, size: 6, font: fontRegular, color: colorEje });
    }
    pagina.drawText(eje2.etiqueta, { x: origen.x + MARGEN_IZQ - 2, y: plotBottom - 8, size: 6, font: fontRegular, color: colorEje });
  }
  for (const v of marcasEje(g.xMin, g.xMax, g.pasoX)) {
    const xx = xDe(v);
    pagina.drawLine({ start: { x: xx, y: plotBottom }, end: { x: xx, y: plotTop }, thickness: 0.25, color: BORDE_SUTIL });
    pagina.drawText(formato(v), { x: xx - formato(v).length * 1.6, y: plotBottom - 8, size: 6, font: fontRegular, color: MUTED });
  }
  pagina.drawText(g.ejeXEtiqueta, { x: plotX + plotW / 2 - g.ejeXEtiqueta.length * 1.8, y: plotBottom - 17, size: 6.5, font: fontRegular, color: GRIS_OSCURO });
  // La unidad del eje Y va dentro del recuadro, arriba a la izquierda (fuera chocaba con la nota).
  pagina.drawText(g.ejeYEtiqueta, { x: plotX + 3, y: plotTop - 8, size: 6.5, font: fontRegular, color: GRIS_OSCURO });

  const rotulos: { texto: string; x: number; y: number }[] = [];
  // Referencia primero (debajo) y paciente al final (encima).
  const orden = [...g.series].sort((a, b) => Number(a.estilo === 'paciente') - Number(b.estilo === 'paciente'));
  for (const serie of orden) {
    const puntos = [...serie.puntos].sort((a, b) => a.x - b.x).filter((p) => p.x >= g.xMin - 1e-9 && p.x <= g.xMax + 1e-9);
    if (puntos.length === 0) continue;
    const esPaciente = serie.estilo === 'paciente';
    const color = serie.color ? COLORES_SERIE[serie.color] : esPaciente ? PINE : GRIS_OSCURO;
    const yS = (v: number): number => yDe(serie.ejeSecundario ? aY2(v) : v);
    for (let i = 1; i < puntos.length; i++) {
      pagina.drawLine({
        start: { x: xDe(puntos[i - 1]!.x), y: yS(puntos[i - 1]!.y) },
        end: { x: xDe(puntos[i]!.x), y: yS(puntos[i]!.y) },
        thickness: esPaciente ? 1.2 : 0.5,
        color,
        ...(serie.estilo === 'referenciaDiscontinua' ? { dashArray: [2.5, 1.5] } : {}),
      });
    }
    if (esPaciente) for (const p of puntos) pagina.drawCircle({ x: xDe(p.x), y: yS(p.y), size: 2, color });
    else {
      const ultimo = puntos[puntos.length - 1]!;
      rotulos.push({ texto: serie.etiqueta, x: xDe(ultimo.x) + 2, y: yS(ultimo.y) - 2 });
    }
  }
  // Rótulos al final de cada línea de referencia, separados al menos 6 pt para que no se encimen donde las curvas se juntan.
  rotulos.sort((a, b) => a.y - b.y);
  for (let i = 1; i < rotulos.length; i++) rotulos[i]!.y = Math.max(rotulos[i]!.y, rotulos[i - 1]!.y + 6);
  for (const r of rotulos) pagina.drawText(r.texto, { x: r.x, y: r.y, size: 5.5, font: fontRegular, color: INK });
}
